import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import PageHeader from '../components/ui/PageHeader';
import CTASection from '../components/home/CTASection';
import * as FiIcons from 'react-icons/fi';

const { FiShield, FiTarget, FiSearch, FiAward } = FiIcons;

const HEADER_IMG = "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?auto=format&fit=crop&q=80";

const coreValues = [
  {
    icon: FiShield,
    title: "Integrity",
    desc: "I do the job right, even when no one is watching."
  },
  {
    icon: FiTarget,
    title: "Discipline",
    desc: "I never cut corners or rush quality."
  },
  {
    icon: FiSearch,
    title: "Attention to Detail",
    desc: "The little things matter because they create extraordinary results."
  },
  {
    icon: FiAward,
    title: "Commitment to Excellence",
    desc: "Every vehicle leaves better than it arrived."
  }
];

export default function About() {
  // Dynamically inject SEO metadata specifically for the About page
  useEffect(() => {
    document.title = "Veteran-Owned Auto Detailing in Jacksonville, FL | About A.R.'s Elite Detailing";
    
    const setMeta = (name, content, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let tag = document.querySelector(`meta[${attr}="${name}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute(attr, name);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    };

    const description = "Meet Arturio Robertson, founder of A.R.'s Elite Detailing. A retired United States Navy veteran delivering premium mobile auto detailing in Jacksonville, Florida with military precision, integrity, and attention to detail.";
    
    setMeta('description', description);
    setMeta('og:title', document.title, true);
    setMeta('og:description', description, true);
    setMeta('twitter:title', document.title);
    setMeta('twitter:description', description);
  }, []);

  return (
    <div className="bg-slate-950">
      <PageHeader 
        title="Veteran-Owned. Mission-Driven. Detail-Obsessed." 
        subtitle="The story and standard behind A.R.'s Elite Detailing." 
        bgImage={HEADER_IMG} 
      />

      <div className="py-24">
        {/* Biography Introduction */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mb-20">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="prose prose-lg prose-invert max-w-none text-slate-300 leading-relaxed"
          >
            <p className="text-2xl font-medium text-white mb-8 leading-snug">
              Some people found detailing through a hobby. I found it through 21 years of service.
            </p>
            <p>
              My name is Arturio Robertson, founder of A.R.'s Elite Detailing LLC and a proud retired United States Navy veteran. Before launching this business, I spent over two decades serving our country as a Culinary Specialist, where precision, discipline, cleanliness, and attention to detail weren't just encouraged—they were the standard.
            </p>
            <p>
              In the military, I learned that the smallest details often make the biggest difference. Every meal prepared, every workspace inspected, and every mission completed demanded pride, consistency, and excellence. Those habits became second nature, and when I retired from the Navy, I knew I wanted to build something that reflected those same values.
            </p>
            <p>
              That's how A.R.'s Elite Detailing was born.
            </p>
            <p>
              For me, detailing isn't simply washing cars. It's restoring pride in something you've worked hard to own. It's bringing back that feeling you had the day you drove it off the lot. It's treating every vehicle—whether it's a family SUV, a work truck, a luxury vehicle, or your weekend toy—with the same level of care and respect I'd expect for my own.
            </p>
            <p className="font-bold text-white mt-10">
              As a veteran-owned business, I operate with the principles that guided me throughout my military career:
            </p>
          </motion.div>
        </div>

        {/* Core Values Grid */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-20">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {coreValues.map((value, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-slate-900 border border-slate-800 p-8 rounded-sm hover:border-blue-500/50 transition-colors group"
              >
                <div className="w-14 h-14 bg-slate-950 border border-slate-800 rounded-sm flex items-center justify-center mb-6 group-hover:bg-blue-600 group-hover:border-blue-500 transition-colors">
                  <value.icon className="text-2xl text-blue-500 group-hover:text-white transition-colors" />
                </div>
                <h4 className="text-xl font-bold text-white mb-3 uppercase tracking-wide">{value.title}</h4>
                <p className="text-slate-400 text-sm leading-relaxed">{value.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Mission Statement & Closing */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="prose prose-lg prose-invert max-w-none text-slate-300 leading-relaxed mb-16"
          >
            <p>
              Mobile detailing also means respecting your time. I bring professional-grade equipment, premium products, and meticulous workmanship directly to your home or workplace, allowing you to focus on your day while I transform your vehicle.
            </p>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, scale: 0.98 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="bg-blue-600 p-8 md:p-12 rounded-sm shadow-2xl relative overflow-hidden mb-16"
          >
            {/* Background Accent */}
            <div className="absolute -top-24 -right-24 w-64 h-64 bg-white opacity-5 rounded-full blur-3xl pointer-events-none" />
            
            <h3 className="text-2xl font-black text-white uppercase tracking-tight mb-4 relative z-10">Our Mission</h3>
            <p className="text-blue-100 text-lg md:text-xl font-medium leading-relaxed relative z-10">
              My mission is simple: provide an elite detailing experience built on trust, professionalism, and military precision. Every customer isn't just another appointment—they're an opportunity to exceed expectations and build a lasting relationship.
            </p>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-slate-300 text-lg leading-relaxed"
          >
            <p>
              Thank you for supporting a veteran-owned small business. I look forward to showing you what military attention to detail really looks like.
            </p>

            <div className="mt-16 pt-12 border-t border-slate-800">
              <h4 className="text-3xl md:text-4xl font-black text-white uppercase tracking-tight mb-8 italic text-blue-500">
                "Pull Up Dirty…<br />Leave Legendary."
              </h4>
              <div className="flex items-center gap-4">
                <div className="w-12 h-1 bg-blue-600" />
                <div>
                  <p className="text-white font-black text-xl uppercase tracking-wide mb-1">Arturio Robertson</p>
                  <p className="text-slate-400 text-xs uppercase tracking-widest font-bold mb-1">Founder & Owner</p>
                  <p className="text-slate-500 text-xs uppercase tracking-widest">A.R.'s Elite Detailing LLC</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      <CTASection />
    </div>
  );
}